import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch, Checkbox, Input } from "@progress/kendo-react-inputs";
import { updateCurrUser } from "../../../../../actions/auth";
import { configurePlanMyDay } from "../../../../../shared/services/user.service";
import { useParams } from "react-router-dom";
import { getIcon } from "../../../../../utils";
import { userDropdownData } from "../../../layout.constants";
import { label, number } from "../../../../../config";
import { updateProfile } from "../../../../../actions/userProfile";

/**
 * OtherSettingsSection component for Other Settings section
 * Displays Plan My Day toggle and related settings
 * @author Bhavana
 */
const OtherSettingsSection = () => {
  const [planMydayActive, setPlanMyDatActive] = useState(false);
  const [dueTaskIn, setDueTaskIn] = useState(null);
  const [includeNoDueDate, setIncludeNoDueDateTasks] = useState(false);
  const [reminderTime, setReminderTime] = useState("");
  const [profileData, setProfileData] = useState(userDropdownData.PROFILE_MODAL);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { uId } = useParams();
  const { visitingByAdmin, details } = useSelector((state) => state.userDetails);

  /**
   * Sets initial state for plan my day and reminder time
   * @author Bhavana
   */
  useEffect(() => {
    setPlanMyDatActive(user?.PlanMyDay ? true : false);
    setDueTaskIn(user?.PlannedTaskDueDays);
    setIncludeNoDueDateTasks(user?.IncludeNoDueDate ? true : false);
    setReminderTime(user?.UserReminderTime ?? "");
  }, [user?.PlanMyDay, user?.PlannedTaskDueDays, user?.IncludeNoDueDate, user?.UserReminderTime]);

  /**
   * Checks if Plan My Day is enabled
   * @param {string} key - The toggle key
   * @returns {boolean} The state of the toggle key
   * @author Bhavana
   */
  const checkFunction = (key) => {
    if (key === "PlanMyDay") return planMydayActive;
    return false;
  };

  /**
   * Plan my day switch input handler
   * @param {boolean} value - The new value of the switch
   * @author Bhavana
   */
  const togglePlanMyDay = (value) => {
    setPlanMyDatActive(value);
    const payload = {
      isPlanMyDayActive: value ? number.ONE : number.ZERO,
      plannedTaskDueDays: number.THREE,
      includeDueDate: number.ZERO,
      userId: user.id,
    };
    const updatedUser = { PlanMyDay: value ? number.ONE : number.ZERO, IncludeNoDueDate: number.ZERO, PlannedTaskDueDays: number.THREE };
    dispatch(updateCurrUser(updatedUser));
    configurePlanMyDay(payload);
  };

  /**
   * Planned task due days input handler
   * @param {Object} e - The event object
   * @author Bhavana
   */
  const plannedTaskChangeHandler = (e) => {
    if (e.target.value < number.ZERO) return;
    setDueTaskIn(e.target.value);
    const payload = { isPlanMyDayActive: planMydayActive, plannedTaskDueDays: e.target.value, includeDueDate: includeNoDueDate, userId: user.id };
    const updatedUser = { PlannedTaskDueDays: e.target.value };
    dispatch(updateCurrUser(updatedUser));
    configurePlanMyDay(payload);
  };

  /**
   * Include due date checkbox handler
   * @param {Object} e - The event object
   * @author Bhavana
   */
  const includeNoDueDateHandler = (e) => {
    setIncludeNoDueDateTasks(e.value);
    const payload = { isPlanMyDayActive: planMydayActive, plannedTaskDueDays: dueTaskIn, includeDueDate: e.value, userId: user.id };
    const updatedUser = { IncludeNoDueDate: e.value ? number.ONE : number.ZERO };
    dispatch(updateCurrUser(updatedUser));
    configurePlanMyDay(payload);
  };

  /**
   * Handles the change of reminder time
   * @param {Object} event - The event object
   * @author Bhavana
   */
  const handleReminderTimeChange = async (event) => {
    const { value } = event.target;
    setReminderTime(value);
    const reminderUpdateResponse = await updateProfile({ userId: user?.id, defaultReminderTime: value });
    if (reminderUpdateResponse.affectedRows > 0) {
      dispatch(updateCurrUser({ UserReminderTime: value }));
    }
  };

  const planMyDayField = profileData?.optionsData?.find((field) => field.key === "PlanMyDay");

  return (
    <div className="profile-section-content bg-light">
      <div className="mt-1">
        {planMyDayField && (
          <div>
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2">
                  {getIcon(planMyDayField.icon)}
                  <div>
                    <div className="font-weight-medium">{planMyDayField.label}</div>
                  </div>
                </div>
              </div>
              <div>
                <Switch
                  size="small"
                  checked={checkFunction(planMyDayField.key)}
                  onChange={({ value }) => {
                    togglePlanMyDay(value);
                  }}
                  disabled={uId}
                  id="userprofilesidebar-plan-my-day-switch"
                />
              </div>
            </div>
          </div>
        )}

        {/* Plan My Day Settings - Shown when active */}
        {planMydayActive && (
          <div>
            <div className="row mt-3">
              {/* Left Column - Show Tasks due in */}
              <div className="col-md-6 pr-3">
                <label htmlFor="userprofilesidebar-plan-my-day-date-input" className="mb-1">
                  Show Tasks due in
                </label>
                <div className="position-relative">
                  <Input
                    id="userprofilesidebar-plan-my-day-date-input"
                    type="number"
                    value={dueTaskIn}
                    onChange={plannedTaskChangeHandler}
                    disabled={uId}
                    className="w-100 user-profile-days-input"
                  />
                  <span className="position-absolute user-profile-days-label">
                    Days
                  </span>
                </div>
              </div>
              
              {/* Right Column - Default Reminder Time (when Plan My Day is active) */}
              <div className="col-md-6 pl-3">
                <label className="mb-1">{label.DEFAULT_REMINDER_TIME}</label>
                <div className="position-relative">
                  <Input
                    placeholder="00:00"
                    name="reminderTime"
                    value={reminderTime}
                    onChange={handleReminderTimeChange}
                    disabled={uId}
                    type="time"
                    className="w-100"
                    id="userprofilesidebar-reminder-time"
                  />
                </div>
              </div>
            </div>

            <div className="d-flex mt-3 align-items-center">
              <label htmlFor="userprofilesidebar-plan-my-day-no-due-date-checkbox" className="mb-0 mr-2 font-weight-normal">
                Include tasks without due date
              </label>
              <Checkbox
                id="userprofilesidebar-plan-my-day-no-due-date-checkbox"
                checked={includeNoDueDate}
                onChange={includeNoDueDateHandler}
                disabled={uId}
              />
            </div>
          </div>
        )}

        {/* Default Reminder Time - Always visible (shown alone when Plan My Day is disabled) */}
        {!planMydayActive && (
          <div className="mt-3">
            <label className="mb-1">{label.DEFAULT_REMINDER_TIME}</label>
            <div className="position-relative">
              <Input
                placeholder="00:00"
                name="reminderTime"
                value={reminderTime}
                onChange={handleReminderTimeChange}
                disabled={uId}
                type="time"
                className="w-50"
                id="userprofilesidebar-reminder-time"
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default OtherSettingsSection;

